import React from 'react'
import type { Block } from '../../editor/types'
import { Star, Sparkles, Circle, Diamond, Minus, MoreHorizontal, Zap, Heart } from 'lucide-react'

const ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  star: Star,
  sparkles: Sparkles,
  circle: Circle,
  diamond: Diamond,
  dash: Minus,
  dots: MoreHorizontal,
  zap: Zap,
  heart: Heart,
}

interface DividerBlockProps {
  block: Block
}

export default function DividerBlock({ block }: DividerBlockProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const style = (config.style as string) || 'solid'
  const height = (config.height as number) || 32
  const color = (config.color as string) || '#e2e8f0'
  const accent = block.accent || 'var(--accent, #5048e5)'

  if (style === 'none') {
    return <div style={{ height }} />
  }

  if (style === 'gradient') {
    return (
      <div className="flex items-center" style={{ height }}>
        <div
          className="w-full h-px"
          style={{
            background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
          }}
        />
      </div>
    )
  }

  if (style === 'wave') {
    return (
      <div className="flex items-center" style={{ height }}>
        <svg className="w-full" height="8" viewBox="0 0 400 8" preserveAspectRatio="none">
          <path
            d="M0,4 C25,0 50,8 75,4 C100,0 125,8 150,4 C175,0 200,8 225,4 C250,0 275,8 300,4 C325,0 350,8 375,4 C400,0 400,4 400,4"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
          />
        </svg>
      </div>
    )
  }

  if (style === 'zigzag') {
    return (
      <div className="flex items-center" style={{ height }}>
        <svg className="w-full" height="8" viewBox="0 0 400 8" preserveAspectRatio="none">
          <path
            d="M0,4 L10,0 L20,8 L30,0 L40,8 L50,0 L60,8 L70,0 L80,8 L90,0 L100,8 L110,0 L120,8 L130,0 L140,8 L150,0 L160,8 L170,0 L180,8 L190,0 L200,8 L210,0 L220,8 L230,0 L240,8 L250,0 L260,8 L270,0 L280,8 L290,0 L300,8 L310,0 L320,8 L330,0 L340,8 L350,0 L360,8 L370,0 L380,8 L390,0 L400,4"
            fill="none"
            stroke={color}
            strokeWidth="1.5"
          />
        </svg>
      </div>
    )
  }

  if (style === 'icon') {
    const iconName = (config.icon as string) || 'star'
    const Icon = ICON_MAP[iconName] || Star
    return (
      <div className="flex items-center gap-3" style={{ height }}>
        <div className="flex-1 h-px" style={{ backgroundColor: color }} />
        <span style={{ color: accent }}><Icon className="h-4 w-4 shrink-0" /></span>
        <div className="flex-1 h-px" style={{ backgroundColor: color }} />
      </div>
    )
  }

  return (
    <div className="flex items-center" style={{ height }}>
      <hr
        className="w-full border-0"
        style={{
          borderTop: `1px ${style} ${color}`,
        }}
      />
    </div>
  )
}
